import { memo, useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Ban, ChevronLeft, Heart, Search, Trash2, X } from 'lucide-react'
import { createUserStyle, favoriteKey, type CreateStyleFormData, getStyleImageUrl, type StyleSummary } from '../../../../api/styles'
import { useStyleDetail, useStyles, useUserStyleDetail, useUserStylesQuery, useFavoriteStyles, useToggleFavorite, queryKeys } from '../../../../api/hooks'
import {
  DISABLED_FILTER_TABS,
  DEFAULT_STYLE,
  NO_STYLE_ID,
  STYLE_GROUPS,
  STYLE_GROUP_FALLBACK,
  STYLE_FAVOURITES,
} from '../../../../constants/styles'
import { localizeStyleName, localizeStyleGroup } from '../../../../i18n/styleLabels'
import { useAuth } from '../../../../contexts/AuthContext'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { StyleLightbox } from './StyleLightbox'
import { DeleteStyleDialog } from './DeleteStyleDialog'
import { CreateStyleCard } from './CreateStyleCard'
import { StyleCreatorForm } from './StyleCreatorForm'
import { StyleCreatorProgress } from './StyleCreatorProgress'
import { StyleCreatorSuccess } from './StyleCreatorSuccess'
import { StyleCreatorError } from './StyleCreatorError'
import { createNadDebug } from '../../../../utils/debugLog'
import { trackInteraction } from '../../../../analytics/interactionAnalytics'
import { useOptionalProjectId } from '../../../../contexts/ProjectIdContext'

const stickyLog = createNadDebug('dw:style-creator-sticky')
const previewNameLog = createNadDebug('dw:style-creator-preview-name')
const searchLog = createNadDebug('dw:style-search')
const favLog = createNadDebug('dw:style-favourites')
const orderLog = createNadDebug('dw:style-fav-order')

function publicStyleDisplayName(style: StyleSummary, t: Parameters<typeof localizeStyleName>[2]): string {
  return style.is_static_fallback ? localizeStyleName(style.id, style.name, t) : style.name
}

type CreationStep = 'analyzing' | 'synthesizing' | 'generating_images' | 'finalizing'

type StyleLibraryView =
  | { mode: 'browse' }
  | { mode: 'create' }
  | { mode: 'creating'; step: CreationStep; progress: number; styleName: string }
  | { mode: 'success'; style: StyleSummary }
  | { mode: 'error'; message: string; formData: CreateStyleFormData }

function toCreationStep(step: string): CreationStep {
  return step === 'synthesizing' || step === 'generating_images' || step === 'finalizing'
    ? step
    : 'analyzing'
}

/**
 * Resolve a style's single group. Group membership lives in the free-form
 * `tags` array; a style is expected to carry exactly one STYLE_GROUPS value.
 * We take the first recognized group in STYLE_GROUPS order so the choice is
 * deterministic and — critically — the SAME rule is used by both the "All"
 * view bucketing and the group-pill filter, so a style can never appear under
 * one group in "All" but a different pill. Returns null if no group is present.
 */
function resolveStyleGroup(tags: string[] | undefined): string | null {
  const set = new Set(tags ?? [])
  for (const group of STYLE_GROUPS) {
    if (set.has(group)) return group
  }
  return null
}

/** Normalize for accent-/case-insensitive substring matching. */
function normalizeForSearch(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
}

/**
 * Client-side style search (Workplace post 1556548202769144). The haystack is
 * the localized display name + the style's slug id (English-ish, so a query in
 * English still matches while the UI is in another locale) + description +
 * tags. Every whitespace token of the query must be present (AND). Semantic
 * matching (e.g. "Coraline" → "Gothic Stop Motion") is intentionally out of
 * scope — only literal text the style already carries is matched.
 */
function styleMatchesQuery(
  style: StyleSummary,
  displayName: string,
  normalizedTokens: string[],
): boolean {
  if (normalizedTokens.length === 0) return true
  const haystack = normalizeForSearch(
    [displayName, style.id, style.description ?? '', (style.tags ?? []).join(' ')].join(' '),
  )
  return normalizedTokens.every((tok) => haystack.includes(tok))
}

/**
 * Stable "favourites first" partition: favourited items keep their relative
 * order and move ahead of the rest (which also keep their relative order).
 * Short-circuits to the SAME array reference when there are zero or all
 * favourites, so the no-favourites path is a strict no-op (identical order and
 * referential identity — memo consumers don't needlessly recompute). Used by
 * both the flat grids and — because grouped bucketing iterates the partitioned
 * list in order — the "All" view's per-group sections.
 */
export function partitionFavouritesFirst<T extends { id: string }>(
  list: T[],
  isFav: (item: T) => boolean,
): T[] {
  const favs = list.filter(isFav)
  if (favs.length === 0 || favs.length === list.length) return list
  return [...favs, ...list.filter((item) => !isFav(item))]
}

function callStyleSelectionCallback(
  callback: ((styleId: string, style?: StyleSummary) => void) | undefined,
  styleId: string,
  style?: StyleSummary,
) {
  if (!callback) return
  if (style) {
    callback(styleId, style)
  } else {
    callback(styleId)
  }
}

export interface StyleLibraryModalProps {
  isOpen: boolean
  onClose: () => void
  currentStyleId: string
  /** Canvas mode: X-close applies selection via onApply(selectedStyleId). */
  onApply?: (styleId: string, style?: StyleSummary) => void
  /** Landing mode: clicking a card immediately calls onSelect(styleId). */
  onSelect?: (styleId: string, style?: StyleSummary) => void
}

/**
 * Full-screen centered Style Library Modal shared by canvas nodes and
 * the landing page.
 *
 * Canvas mode — all dismiss paths (X button, Escape, backdrop click)
 * save the current selection via onApply(selectedStyleId) when the user
 * has clicked a style card. If no card was clicked, they simply close
 * via onClose().
 *
 * Landing mode:
 * - Clicking a card immediately calls onSelect(styleId)
 * - X button / Escape / backdrop click all call onClose()
 */
const StyleLibraryModal = memo(({
  isOpen,
  onClose,
  onApply,
  onSelect,
  currentStyleId,
}: StyleLibraryModalProps) => {
  const { t } = useTranslation('canvas')
  // canvas_style_library_action: only inside a project. The landing Style
  // Library (no project yet) keeps landing_prompt_option_action.
  const analyticsProjectId = useOptionalProjectId()
  const trackStyle = useCallback((action: 'select' | 'apply' | 'create_submitted' | 'use_created' | 'favorite_on' | 'favorite_off' | 'delete_confirmed', styleId?: string) => {
    if (analyticsProjectId) trackInteraction('canvas_style_library_action', { project_id: analyticsProjectId, action, style_id: styleId })
  }, [analyticsProjectId])
  // Suspend canvas keyboard shortcuts while this overlay is open (see canvasShortcutGuard).
  useBlockingOverlay(isOpen)
  const queryClient = useQueryClient()
  const { data: stylesData, isLoading } = useStyles(isOpen)
  const styles = useMemo(() => stylesData?.styles ?? [], [stylesData])
  const { data: userStylesData, isLoading: isLoadingUserStyles } = useUserStylesQuery(isOpen)
  const userStyles = useMemo(() => userStylesData?.styles ?? [], [userStylesData])
  const { isAuthenticated } = useAuth()
  const { data: favoritesData, isLoading: isLoadingFavorites } = useFavoriteStyles(
    isOpen && isAuthenticated,
  )
  const favoriteRefs = useMemo(() => favoritesData?.favorites ?? [], [favoritesData])
  const favoriteKeySet = useMemo(
    () => new Set(favoriteRefs.map(favoriteKey)),
    [favoriteRefs],
  )
  // Source-qualified favourite lookups — one place that knows the key format.
  const isPublicFavourite = useCallback(
    (id: string) =>
      id !== NO_STYLE_ID && favoriteKeySet.has(favoriteKey({ source: 'public', style_id: id })),
    [favoriteKeySet],
  )
  const isUserFavourite = useCallback(
    (id: string) => favoriteKeySet.has(favoriteKey({ source: 'user', style_id: id })),
    [favoriteKeySet],
  )
  // Favourites-first ordering (and the pinned Favourites section) apply only to
  // authenticated users. Sign-out calls invalidateQueries() (not clear), so the
  // now-disabled useFavoriteStyles can still expose stale cached favourites;
  // gating on auth here prevents a signed-out user retaining personalized order
  // while their hearts / Favourites tab disappear.
  const orderingEnabled = isAuthenticated
  const toggleFavorite = useToggleFavorite()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeTag, setActiveTag] = useState<string>('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [hasUserSelected, setHasUserSelected] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [view, setView] = useState<StyleLibraryView>({ mode: 'browse' })
  const autoNavTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wasOpenRef = useRef(false)

  // Reset modal state only on the closed→open transition. Re-running on every
  // styles/userStyles array-ref change would clobber views like 'success' when
  // React Query refetches after a mutation (e.g. createUserStyle invalidating
  // userStyles).
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const exists = styles.some((s) => s.id === currentStyleId)
        || userStyles.some((s) => s.id === currentStyleId)
      setSelectedId(exists ? currentStyleId : null)
      setHasUserSelected(false)
      setActiveTag('All')
      setSearchQuery('')
      setLightboxUrl(null)
      setView({ mode: 'browse' })
    }
    if (!isOpen && autoNavTimerRef.current) {
      clearTimeout(autoNavTimerRef.current)
      autoNavTimerRef.current = null
    }
    wasOpenRef.current = isOpen
  }, [isOpen, styles, userStyles, currentStyleId])

  useEffect(() => {
    return () => {
      if (autoNavTimerRef.current) {
        clearTimeout(autoNavTimerRef.current)
      }
    }
  }, [])

  // Find selected style for preview and for optional parent callback metadata.
  const selectedStyle = useMemo(
    () => styles.find((s) => s.id === selectedId) || userStyles.find((s) => s.id === selectedId),
    [styles, userStyles, selectedId],
  )
  const selectedUserStyle = useMemo(
    () => userStyles.find((s) => s.id === selectedId),
    [userStyles, selectedId],
  )

  // Shared dismiss handler — applies the user's selection (if any) then closes.
  // Used by X button, backdrop click, and Escape key.
  const handleDismiss = useCallback(() => {
    if (onApply && hasUserSelected && selectedId) {
      trackStyle('apply', selectedId)
      callStyleSelectionCallback(onApply, selectedId, selectedUserStyle)
    } else {
      onClose()
    }
  }, [hasUserSelected, selectedId, selectedUserStyle, onApply, onClose, trackStyle])

  const handleSaveAndClose = useCallback(() => {
    if (onApply && selectedId) {
      trackStyle('apply', selectedId)
      callStyleSelectionCallback(onApply, selectedId, selectedUserStyle)
    } else {
      onClose()
    }
  }, [selectedId, selectedUserStyle, onApply, onClose, trackStyle])

  // Escape key handler — skips when lightbox is open (lightbox handles its own Escape)
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If lightbox is open, it will handle Escape via its own capture-phase listener
        if (lightboxUrl) return
        e.stopPropagation()
        handleDismiss()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleDismiss, lightboxUrl])

  const handleCardClick = useCallback((styleId: string) => {
    const nextStyleId = selectedId === styleId ? DEFAULT_STYLE : styleId
    const nextUserStyle = nextStyleId === DEFAULT_STYLE
      ? undefined
      : userStyles.find((style) => style.id === nextStyleId)
    setSelectedId(nextStyleId)
    setHasUserSelected(true)
    trackStyle('select', nextStyleId)
    // In landing mode, immediately notify the parent
    callStyleSelectionCallback(onSelect, nextStyleId, nextUserStyle)
  }, [onSelect, selectedId, userStyles, trackStyle])

  const handleToggleFavorite = useCallback(
    (source: 'public' | 'user', styleId: string, next: boolean) => {
      favLog({
        event: 'toggle',
        feature: 'style-library-search-favourites',
        source,
        styleId,
        next,
      })
      trackStyle(next ? 'favorite_on' : 'favorite_off', styleId)
      toggleFavorite.mutate({ ref: { source, style_id: styleId }, next })
    },
    [toggleFavorite, trackStyle],
  )

  const handleStyleDeleted = useCallback(() => {
    trackStyle('delete_confirmed', deleteTarget?.id)
    queryClient.invalidateQueries({ queryKey: queryKeys.userStyles })
    if (deleteTarget && deleteTarget.id === currentStyleId && onApply) {
      onApply(DEFAULT_STYLE)
    }
  }, [deleteTarget, currentStyleId, onApply, queryClient, trackStyle])

  const handleCreateSubmit = useCallback(async (data: CreateStyleFormData) => {
    trackStyle('create_submitted')
    setView({ mode: 'creating', step: 'analyzing', progress: 0, styleName: data.name })
    try {
      const style = await createUserStyle(data, (step, progress) => {
        setView({ mode: 'creating', step: toCreationStep(step), progress, styleName: data.name })
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.userStyles })
      setView({ mode: 'success', style })
      stickyLog({ event: 'success-view-mounted', styleId: style.id, styleName: style.name, sticky: true })
      previewNameLog({
        event: 'style-create-success-mounted',
        feature: 'style-creator-preview-name',
        styleId: style.id,
        styleName: style.name,
        hasThumbnail: Boolean(style.thumbnail_url),
        previewCount: style.preview_image_urls.length,
      })
    } catch (err) {
      setView({ mode: 'error', message: err instanceof Error ? err.message : 'An unexpected error occurred', formData: data })
    }
  }, [queryClient, trackStyle])

  const handleUseStyle = useCallback((styleId: string, style?: StyleSummary) => {
    if (autoNavTimerRef.current) {
      clearTimeout(autoNavTimerRef.current)
      autoNavTimerRef.current = null
    }
    stickyLog({ event: 'use-this-style-clicked', styleId })
    trackStyle('use_created', styleId)
    previewNameLog({
      event: 'style-create-use-style',
      feature: 'style-creator-preview-name',
      styleId,
      styleName: style?.name,
    })
    callStyleSelectionCallback(onApply, styleId, style)
    callStyleSelectionCallback(onSelect, styleId, style)
    setView({ mode: 'browse' })
  }, [onApply, onSelect, trackStyle])

  const handleBackToBrowse = useCallback(() => {
    if (autoNavTimerRef.current) {
      clearTimeout(autoNavTimerRef.current)
      autoNavTimerRef.current = null
    }
    setView({ mode: 'browse' })
  }, [])

  // ---- Unified view model: base-set-by-tab, THEN search filter ----
  // Modes never overlap because search is always an AND-narrowing applied on top
  // of whatever the active tab selects (All / a group / Favourites).
  const normalizedTokens = useMemo(
    () => normalizeForSearch(searchQuery.trim()).split(/\s+/).filter(Boolean),
    [searchQuery],
  )
  const isSearching = normalizedTokens.length > 0
  const isFavTab = activeTag === STYLE_FAVOURITES

  // Public styles for the active tab (before search). "All" = every group
  // (rendered as titled sections below); a group pill narrows to that group;
  // Favourites narrows to favourited public styles. A style's group is resolved
  // via resolveStyleGroup — the SAME rule the "All" view uses — so filtering and
  // grouping never disagree for a style carrying >1 group tag.
  const publicForTab = useMemo(() => {
    if (isFavTab) {
      return styles.filter((s) => isPublicFavourite(s.id))
    }
    if (activeTag === 'All') return styles
    return styles.filter((s) => resolveStyleGroup(s.tags) === activeTag)
  }, [styles, activeTag, isFavTab, isPublicFavourite])

  // User styles for the active tab (before search). Group pills do NOT filter My
  // Styles (unchanged behavior); only the Favourites tab narrows them.
  const userForTab = useMemo(() => {
    if (isFavTab) {
      return userStyles.filter((s) => isUserFavourite(s.id))
    }
    return userStyles
  }, [userStyles, isFavTab, isUserFavourite])

  // Search-filter, then float favourites first (stable). The Favourites tab is
  // already all-favourites and intentionally recency-ordered, so skip it there.
  // In the "All" view the partitioned order propagates into each group section
  // (groupedPublicStyles buckets by iterating this list in order).
  const publicToRender = useMemo(() => {
    const filtered = isSearching
      ? publicForTab.filter((s) =>
          styleMatchesQuery(s, publicStyleDisplayName(s, t), normalizedTokens),
        )
      : publicForTab
    if (!orderingEnabled || isFavTab) return filtered
    return partitionFavouritesFirst(filtered, (s) => isPublicFavourite(s.id))
  }, [publicForTab, isSearching, normalizedTokens, t, orderingEnabled, isFavTab, isPublicFavourite])
  const userToRender = useMemo(() => {
    const filtered = isSearching
      ? userForTab.filter((s) => styleMatchesQuery(s, s.name, normalizedTokens))
      : userForTab
    if (!orderingEnabled || isFavTab) return filtered
    return partitionFavouritesFirst(filtered, (s) => isUserFavourite(s.id))
  }, [userForTab, isSearching, normalizedTokens, orderingEnabled, isFavTab, isUserFavourite])

  // Favourites tab: one recency-ordered grid combining public + user favourites
  // (favoriteRefs is server-ordered most-recent-first). Reuses the already
  // search-filtered public/user sets so search narrows favourites too.
  const favoritesToRender = useMemo(() => {
    if (!isFavTab) return []
    const publicById = new Map(publicToRender.map((s) => [s.id, s]))
    const userById = new Map(userToRender.map((s) => [s.id, s]))
    const out: { style: StyleSummary; source: 'public' | 'user' }[] = []
    for (const ref of favoriteRefs) {
      const s =
        ref.source === 'public' ? publicById.get(ref.style_id) : userById.get(ref.style_id)
      if (s) out.push({ style: s, source: ref.source })
    }
    return out
  }, [isFavTab, favoriteRefs, publicToRender, userToRender])

  // For the "All" view (not searching, not favourites), split public styles into
  // per-group titled sections. Graceful degradation: until a style carries a
  // recognized group tag, this returns null and the flat grid renders — the
  // grouped UI lights up automatically once the data has groups.
  const groupedPublicStyles = useMemo(() => {
    if (activeTag !== 'All' || isSearching) return null
    const buckets = new Map<string, typeof styles>()
    for (const group of STYLE_GROUPS) buckets.set(group, [])
    const other: typeof styles = []
    for (const style of publicToRender) {
      const group = resolveStyleGroup(style.tags)
      if (group) buckets.get(group)!.push(style)
      else other.push(style)
    }
    const sections: { group: string; styles: typeof styles }[] = []
    for (const group of STYLE_GROUPS) {
      const items = buckets.get(group)!
      if (items.length) sections.push({ group, styles: items })
    }
    if (sections.length === 0) return null
    if (other.length) sections.push({ group: STYLE_GROUP_FALLBACK, styles: other })
    // Pinned "Favourites" section at the very top of the grouped "All" view.
    // Favourited public styles appear here (recency order, mirroring the
    // Favourites tab) AND remain in their own group sections below (favourites
    // float first within each group via the partitioned publicToRender). This
    // reuses the group-section render + localizeStyleGroup(STYLE_FAVOURITES)
    // label, so it needs no new JSX or i18n string. Uses the same section render
    // path, so keys don't collide: the section key is "Favourites" (absent from
    // STYLE_GROUPS) and card key={style.id} is scoped per-grid.
    if (orderingEnabled) {
      const publicById = new Map(publicToRender.map((s) => [s.id, s]))
      const favPublic: typeof styles = []
      for (const ref of favoriteRefs) {
        if (ref.source !== 'public') continue
        const s = publicById.get(ref.style_id)
        if (s) favPublic.push(s)
      }
      if (favPublic.length) sections.unshift({ group: STYLE_FAVOURITES, styles: favPublic })
    }
    return sections
  }, [publicToRender, activeTag, isSearching, orderingEnabled, favoriteRefs])

  // Filter pills: always "All" + the fixed style groups, plus a Favourites pill
  // for authenticated users. NOT derived from the data payload — on a cold cache
  // or listStyles() failure, useStyles() serves fallbackStyles() (legacy tags),
  // and hiding the group pills there would make the advertised filter bar vanish.
  const visibleFilterTabs = useMemo(
    () => ['All', ...STYLE_GROUPS, ...(isAuthenticated ? [STYLE_FAVOURITES] : [])],
    [isAuthenticated],
  )

  // NAD trace for the search feature — counts/length only, never the query text.
  useEffect(() => {
    if (!isOpen) return
    searchLog({
      event: 'query-changed',
      feature: 'style-library-search-favourites',
      activeTag,
      queryLen: searchQuery.trim().length,
      publicMatches: publicToRender.length,
      userMatches: userToRender.length,
    })
  }, [isOpen, activeTag, searchQuery, publicToRender.length, userToRender.length])

  // If auth is lost while the modal sits on the Favourites tab, fall back to
  // "All". publicForTab/userForTab would otherwise keep filtering on stale
  // cached favourites (sign-out invalidates but does not clear the cache), and
  // the Favourites pill itself is removed for signed-out users.
  useEffect(() => {
    if (!isAuthenticated && activeTag === STYLE_FAVOURITES) {
      setActiveTag('All')
    }
  }, [isAuthenticated, activeTag])

  // NAD trace for favourites-first ordering — counts only, never names/query.
  useEffect(() => {
    if (!isOpen) return
    const publicFavFloated = orderingEnabled
      ? publicToRender.filter((s) => isPublicFavourite(s.id)).length
      : 0
    const userFavFloated = orderingEnabled
      ? userToRender.filter((s) => isUserFavourite(s.id)).length
      : 0
    const pinnedFavCount =
      groupedPublicStyles && groupedPublicStyles[0]?.group === STYLE_FAVOURITES
        ? groupedPublicStyles[0].styles.length
        : 0
    orderLog({
      event: 'reorder',
      feature: 'style-library-search-favourites',
      activeTag,
      orderingEnabled,
      publicFavFloated,
      userFavFloated,
      pinnedFavCount,
    })
  }, [
    isOpen,
    activeTag,
    orderingEnabled,
    publicToRender,
    userToRender,
    groupedPublicStyles,
    isPublicFavourite,
    isUserFavourite,
  ])

  const isSelectedUserStyle = useMemo(
    () => !!selectedId && userStyles.some((s) => s.id === selectedId),
    [selectedId, userStyles],
  )
  const needsPreviewDetail = !!selectedStyle && selectedStyle.preview_image_urls.length < 4
  const { data: publicStyleDetail, isFetching: isFetchingPublicStyleDetail } = useStyleDetail(
    !isSelectedUserStyle ? selectedId : null,
    isOpen && needsPreviewDetail,
  )
  const { data: userStyleDetail, isFetching: isFetchingUserStyleDetail } = useUserStyleDetail(
    isSelectedUserStyle ? selectedId : null,
    isOpen && needsPreviewDetail,
  )
  const selectedStyleDetail = isSelectedUserStyle ? userStyleDetail : publicStyleDetail
  const selectedStylePreviewUrls = selectedStyleDetail?.preview_image_urls.length
    ? selectedStyleDetail.preview_image_urls
    : selectedStyle?.preview_image_urls ?? []
  const isFetchingSelectedStyleDetail = isFetchingPublicStyleDetail || isFetchingUserStyleDetail

  if (!isOpen) return null

  // Section visibility for the non-favourites view. When searching, a section is
  // hidden unless it has matches (or is still loading); if neither section has
  // matches we show a single "no results" message instead.
  const showPublicSection = isLoading || !isSearching || publicToRender.length > 0
  const showMyStylesSection =
    isAuthenticated && (isLoadingUserStyles || !isSearching || userToRender.length > 0)
  const showSearchEmpty =
    isSearching &&
    !isLoading &&
    !isLoadingUserStyles &&
    publicToRender.length === 0 &&
    userToRender.length === 0

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      data-testid="style-library-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={handleDismiss}
        data-testid="style-modal-backdrop"
      />

      {/* Modal content */}
      <div
        className="relative z-10 bg-surface-2 border border-border rounded-xl shadow-2xl w-[90vw] max-w-[1000px] max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            {view.mode !== 'browse' && view.mode !== 'creating' && (
              <button
                onClick={handleBackToBrowse}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-white/10 transition-colors"
                data-testid="style-modal-back-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-lg font-medium text-foreground" data-testid="style-modal-title">
              {view.mode === 'browse' && t('styleLibraryModal.title')}
              {view.mode === 'create' && t('styleLibraryModal.createTitle')}
              {/* view.styleName is a user-typed name — interpolated, never translated. */}
              {view.mode === 'creating' && t('styleLibraryModal.creatingTitle', { styleName: view.styleName })}
              {view.mode === 'success' && t('styleLibraryModal.successTitle')}
              {view.mode === 'error' && t('styleLibraryModal.createTitle')}
            </h2>
          </div>
          <button
            onClick={handleDismiss}
            aria-label={t('styleLibraryModal.close')}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-surface-3 transition-colors"
            data-testid="style-modal-close-btn"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {view.mode === 'browse' && (
          <>
            {/* Search + filter bar */}
            <div className="px-10 pt-5 flex-shrink-0 space-y-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('styleLibraryModal.searchPlaceholder')}
                  aria-label={t('styleLibraryModal.searchAriaLabel')}
                  data-testid="style-search-input"
                  className="h-12 w-full rounded-lg bg-surface-2 border border-border pl-12 pr-11 text-lg text-foreground placeholder:text-muted-foreground transition-colors hover:border-input-border-hover focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    aria-label={t('styleLibraryModal.clearSearch')}
                    data-testid="style-search-clear"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-surface-3 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-thin" data-testid="tag-filter-bar">
                {visibleFilterTabs.map((tag) => {
                  const isActive = activeTag === tag
                  const isDisabled = DISABLED_FILTER_TABS.has(tag)
                  return (
                    <button
                      key={tag}
                      onClick={() => !isDisabled && setActiveTag(tag)}
                      disabled={isDisabled}
                      data-testid={`tag-pill-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                      aria-pressed={isActive}
                      // flex-auto: chips share the row's spare width (mock fills
                      // the full search-bar width) but never shrink below their
                      // label, so the bar still scrolls on narrow viewports.
                      className={`
                        flex-auto flex-shrink-0 h-12 px-4 rounded-lg border text-lg whitespace-nowrap transition-colors duration-200
                        ${isActive
                          ? 'bg-primary/15 text-primary border-primary/70'
                          : isDisabled
                            ? 'bg-surface-2 text-foreground/25 border-border-subtle cursor-not-allowed'
                            : 'bg-surface-2 text-foreground/85 border-border hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground cursor-pointer'
                        }
                      `}
                    >
                      {/* `tag` stays the English group value (filter identity +
                          testid slug); only the visible label is localized. */}
                      {localizeStyleGroup(tag, t)}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Style grid */}
            <div className="px-10 pt-3 pb-5 overflow-y-auto flex-1 min-h-0" data-testid="style-grid">
              {isFavTab ? (
                /* Favourites tab — single recency-ordered grid (public + user). */
                (isLoading || isLoadingUserStyles || isLoadingFavorites) ? (
                  <div className="flex items-center justify-center py-12">
                    <svg className="animate-spin w-6 h-6 text-foreground/30" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                ) : favoritesToRender.length === 0 ? (
                  <div
                    className="flex items-center justify-center py-12 text-foreground/30 text-sm text-center px-6"
                    data-testid="favourites-empty"
                  >
                    {isSearching
                      ? t('styleLibraryModal.searchNoResults')
                      : t('styleLibraryModal.noFavourites')}
                  </div>
                ) : (
                  <div
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-2.5 gap-y-4"
                    data-testid="favourites-grid"
                  >
                    {favoritesToRender.map(({ style, source }) => (
                      <StyleCardInline
                        key={`${source}:${style.id}`}
                        style={style}
                        source={source}
                        displayName={source === 'public' ? publicStyleDisplayName(style, t) : style.name}
                        isSelected={selectedId === style.id}
                        onSelect={handleCardClick}
                        onDelete={
                          source === 'user'
                            ? (styleId) => setDeleteTarget({ id: styleId, name: style.name })
                            : undefined
                        }
                        canFavourite={isAuthenticated}
                        isFavourited
                        favPending={toggleFavorite.isPending}
                        onToggleFavourite={handleToggleFavorite}
                        t={t}
                      />
                    ))}
                  </div>
                )
              ) : showSearchEmpty ? (
                <div
                  className="flex items-center justify-center py-12 text-foreground/30 text-sm"
                  data-testid="search-empty"
                >
                  {t('styleLibraryModal.searchNoResults')}
                </div>
              ) : (
                <>
                  {/* Public Styles section */}
                  {showPublicSection && (
                    <>
                      {/* Grouped "All" view puts the "Public Styles" label on the
                          right of the first group's header row instead. */}
                      {!(groupedPublicStyles && !isLoading && publicToRender.length > 0) && (
                        <SectionHeaderRow
                          left={
                            activeTag !== 'All' && !isSearching ? localizeStyleGroup(activeTag, t) : null
                          }
                          right={t('styleLibraryModal.publicStyles')}
                        />
                      )}
                      {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <svg className="animate-spin w-6 h-6 text-foreground/30" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        </div>
                      ) : publicToRender.length === 0 ? (
                        <div className="flex items-center justify-center py-12 text-foreground/30 text-sm">
                          {t('styleLibraryModal.noStylesForTag')}
                        </div>
                      ) : groupedPublicStyles ? (
                        /* "All" view — one titled section per style group */
                        <div className="flex flex-col gap-6" data-testid="style-group-sections">
                          {groupedPublicStyles.map(({ group, styles: groupStyles }, groupIndex) => (
                            <div key={group} data-testid={`style-group-${group.toLowerCase().replace(/\s+/g, '-')}`}>
                              <SectionHeaderRow
                                // `group` is the English value (section identity +
                                // testid slug); render the localized label.
                                left={localizeStyleGroup(group, t)}
                                leftTestId="style-group-title"
                                right={groupIndex === 0 ? t('styleLibraryModal.publicStyles') : null}
                              />
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-2.5 gap-y-4">
                                {groupStyles.map((style) => (
                                  <StyleCardInline
                                    key={style.id}
                                    style={style}
                                    source="public"
                                    // DB-backed public styles are server-localized; static fallback stays frontend-localized.
                                    displayName={publicStyleDisplayName(style, t)}
                                    isSelected={selectedId === style.id}
                                    onSelect={handleCardClick}
                                    canFavourite={isAuthenticated && style.id !== NO_STYLE_ID}
                                    isFavourited={isPublicFavourite(style.id)}
                                    favPending={toggleFavorite.isPending}
                                    onToggleFavourite={handleToggleFavorite}
                                    t={t}
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-2.5 gap-y-4">
                          {publicToRender.map((style) => (
                            <StyleCardInline
                              key={style.id}
                              style={style}
                              source="public"
                              // DB-backed public styles are server-localized; static fallback stays frontend-localized.
                              displayName={publicStyleDisplayName(style, t)}
                              isSelected={selectedId === style.id}
                              onSelect={handleCardClick}
                              canFavourite={isAuthenticated && style.id !== NO_STYLE_ID}
                              isFavourited={isPublicFavourite(style.id)}
                              favPending={toggleFavorite.isPending}
                              onToggleFavourite={handleToggleFavorite}
                              t={t}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* My Styles section — only shown when authenticated */}
                  {showMyStylesSection && (
                    <>
                      {showPublicSection && <div className="border-t border-border my-6" />}

                      <SectionHeaderRow left={t('styleLibraryModal.myStyles')} />
                      {isLoadingUserStyles ? (
                        <div className="flex items-center justify-center py-8">
                          <svg className="animate-spin w-5 h-5 text-foreground/30" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-2.5 gap-y-4">
                          {/* Create card is hidden while searching (it isn't a search result). */}
                          {!isSearching && (
                            <CreateStyleCard
                              onClick={() => setView({ mode: 'create' })}
                              styleCount={userStyles.length}
                              limit={10}
                            />
                          )}
                          {userToRender.map((style) => (
                            <StyleCardInline
                              key={style.id}
                              style={style}
                              source="user"
                              // User-created styles are content — name shown verbatim.
                              displayName={style.name}
                              isSelected={selectedId === style.id}
                              onSelect={handleCardClick}
                              onDelete={(styleId) => setDeleteTarget({ id: styleId, name: style.name })}
                              canFavourite={isAuthenticated}
                              isFavourited={isUserFavourite(style.id)}
                              favPending={toggleFavorite.isPending}
                              onToggleFavourite={handleToggleFavorite}
                              t={t}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Footer: selected style preview (left) + Cancel / Save (right). */}
            {(selectedStyle || onApply) && (
              <div
                className="flex flex-shrink-0 items-end gap-6 border-t border-border px-10 pt-5 pb-6"
                data-testid="style-modal-footer"
              >
                <div className="min-w-0 flex-1">
                  {selectedStyle && (
                    <div data-testid="style-preview-panel">
                      <h3 className="mb-4 text-sm uppercase tracking-wide text-muted-foreground">
                        {t('styleLibraryModal.styleTemplatePreview')}
                      </h3>
                      <div className="flex gap-2.5 overflow-x-auto pb-1">
                        {selectedStylePreviewUrls.map((url, i) => (
                          <img
                            key={i}
                            src={getStyleImageUrl(url)}
                            alt={`${isSelectedUserStyle ? selectedStyle.name : publicStyleDisplayName(selectedStyle, t)} preview ${i + 1}`}
                            className="h-32 w-32 flex-shrink-0 cursor-pointer rounded-md object-cover transition-all hover:ring-2 hover:ring-primary/40"
                            loading="lazy"
                            onClick={() => setLightboxUrl(getStyleImageUrl(url))}
                            data-testid={`style-preview-image-${i}`}
                          />
                        ))}
                        {selectedStylePreviewUrls.length === 0 && isFetchingSelectedStyleDetail && (
                          <div
                            className="flex h-32 w-32 flex-shrink-0 items-center justify-center rounded-md bg-surface-1"
                            data-testid="style-preview-loading"
                          >
                            <svg className="h-5 w-5 animate-spin text-foreground/30" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {onApply && (
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {/* Cancel discards the pending selection (unlike X / Escape /
                        backdrop, which keep the existing apply-on-dismiss rule). */}
                    <button
                      type="button"
                      onClick={onClose}
                      className="h-12 rounded-lg border border-input-border-hover bg-surface-2 px-4 text-lg text-foreground transition-colors hover:bg-surface-3"
                      data-testid="style-modal-cancel-btn"
                    >
                      {t('styleLibraryModal.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveAndClose}
                      disabled={!selectedId}
                      className="h-12 rounded-lg bg-primary px-4 text-lg font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-surface-3 disabled:text-text-muted"
                      data-testid="style-modal-save-close-btn"
                    >
                      {t('styleLibraryModal.savePlan')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {view.mode === 'create' && (
          <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <StyleCreatorForm
              existingStyleIds={userStyles.map(s => s.id)}
              onSubmit={handleCreateSubmit}
              onCancel={handleBackToBrowse}
            />
          </div>
        )}

        {view.mode === 'creating' && (
          <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <StyleCreatorProgress
              styleName={view.styleName}
              currentStep={view.step}
              progress={view.progress}
            />
          </div>
        )}

        {view.mode === 'success' && (
          <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <StyleCreatorSuccess
              style={view.style}
              onUseStyle={handleUseStyle}
              onBack={handleBackToBrowse}
            />
          </div>
        )}

        {view.mode === 'error' && (
          <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <StyleCreatorError
              message={view.message}
              onRetry={() => handleCreateSubmit(view.formData)}
            />
            <StyleCreatorForm
              existingStyleIds={userStyles.map(s => s.id)}
              onSubmit={handleCreateSubmit}
              onCancel={handleBackToBrowse}
              initialValues={view.formData}
            />
          </div>
        )}

        {/* Lightbox overlay for full-size preview */}
        <StyleLightbox
          imageUrl={lightboxUrl}
          onClose={() => setLightboxUrl(null)}
        />

        {/* Delete confirmation dialog */}
        <DeleteStyleDialog
          isOpen={deleteTarget !== null}
          onClose={() => setDeleteTarget(null)}
          styleId={deleteTarget?.id ?? ''}
          styleName={deleteTarget?.name ?? ''}
          onDeleted={handleStyleDeleted}
        />
      </div>
    </div>
  )

  return createPortal(modal, document.body)
})

StyleLibraryModal.displayName = 'StyleLibraryModal'
export { StyleLibraryModal }

// -- Inline StyleCard (avoid importing from landing) --

interface StyleCardInlineProps {
  style: StyleSummary
  /** Which list this card belongs to — disambiguates favourites (a user slug can
   *  collide with a public slug) and drives the source-qualified test id. */
  source: 'public' | 'user'
  /**
   * Display name to render — localized for built-in styles, verbatim for
   * user-created styles. Computed by the caller so this shared card component
   * stays agnostic of the user-vs-public distinction.
   */
  displayName: string
  isSelected: boolean
  onSelect: (styleId: string) => void
  onDelete?: (styleId: string) => void
  /** Favourites (Workplace post 1556548202769144) — only enabled when authed. */
  canFavourite?: boolean
  isFavourited?: boolean
  favPending?: boolean
  onToggleFavourite?: (source: 'public' | 'user', styleId: string, next: boolean) => void
  t: Parameters<typeof localizeStyleName>[2]
}

/**
 * Card container is a plain <div> with a full-card overlay <button> for
 * selection plus sibling heart/trash <button>s. This avoids nesting interactive
 * elements inside a button (invalid HTML / unreliable keyboard behavior).
 */
const StyleCardInline = memo(
  ({
    style,
    source,
    displayName,
    isSelected,
    onSelect,
    onDelete,
    canFavourite,
    isFavourited,
    favPending,
    onToggleFavourite,
    t,
  }: StyleCardInlineProps) => (
    <div className="group relative flex flex-col" data-selected={isSelected ? 'true' : undefined}>
      {/* Thumbnail — the selection outline lives here; the caption sits below,
          outside the frame. */}
      <div
        className={`
          relative aspect-square w-full overflow-hidden rounded-md border transition-colors duration-200
          ${isSelected ? 'border-primary' : 'border-transparent group-hover:border-primary/50'}
        `}
      >
        {style.thumbnail_url ? (
          <img
            src={getStyleImageUrl(style.thumbnail_url)}
            alt={displayName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : style.id === NO_STYLE_ID ? (
          <div className="flex h-full w-full items-center justify-center bg-surface-1">
            <Ban
              aria-hidden="true"
              data-testid="no-style-symbol"
              className="h-10 w-10 text-muted-foreground"
              strokeWidth={1.5}
            />
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
            <span className="text-foreground/20 text-2xl font-bold">{displayName[0]}</span>
          </div>
        )}
      </div>

      {/* Caption */}
      <span
        className={`mt-1.5 block truncate text-xs transition-colors ${
          isSelected ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
        }`}
      >
        {displayName}
      </span>

      {/* Full-card selection overlay — sits below the corner controls (z-[1]). */}
      <button
        type="button"
        onClick={() => onSelect(style.id)}
        aria-label={displayName}
        aria-pressed={isSelected}
        // Selection test id stays unqualified for back-compat with the existing
        // suite; the source-qualified React key + fav-button test id disambiguate
        // a public/user slug collision. (Pre-existing collision risk unchanged.)
        data-testid={`style-card-${style.id}`}
        className="absolute inset-0 z-[1] cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      />

      {/* Heart toggle (top-left) — above the overlay (z-[2]). */}
      {canFavourite && onToggleFavourite && (
        <button
          type="button"
          onClick={() => onToggleFavourite(source, style.id, !isFavourited)}
          disabled={favPending}
          aria-pressed={isFavourited}
          aria-label={
            isFavourited
              ? t('styleLibraryModal.favouriteRemove')
              : t('styleLibraryModal.favouriteAdd')
          }
          data-testid={`style-card-fav-${source}-${style.id}`}
          className={`
            absolute top-1.5 left-1.5 z-[2] w-7 h-7 rounded-md bg-black/60 flex items-center justify-center
            transition-all disabled:opacity-50
            ${isFavourited ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}
            hover:bg-black/80
          `}
        >
          <Heart
            className={`w-3.5 h-3.5 ${isFavourited ? 'text-primary' : 'text-white'}`}
            fill={isFavourited ? 'currentColor' : 'none'}
          />
        </button>
      )}

      {/* Trash icon for user styles (top-right) — above the overlay (z-[2]). */}
      {onDelete && (
        <button
          type="button"
          onClick={() => onDelete(style.id)}
          className="absolute top-1.5 right-1.5 z-[2] w-7 h-7 rounded-md bg-black/60 hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all"
          aria-label={t('styleLibraryModal.deleteStyle', { name: displayName })}
          data-testid={`style-card-delete-${style.id}`}
        >
          <Trash2 className="w-3.5 h-3.5 text-white" />
        </button>
      )}
    </div>
  ),
)

StyleCardInline.displayName = 'StyleCardInline'

// -- Section header row: group label left, optional secondary label right --

interface SectionHeaderRowProps {
  left?: string | null
  leftTestId?: string
  right?: string | null
}

function SectionHeaderRow({ left, leftTestId, right }: SectionHeaderRowProps) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-4 text-sm uppercase tracking-wide text-muted-foreground">
      {left ? (
        <h4 className="truncate text-sm font-normal text-muted-foreground" data-testid={leftTestId}>
          {left}
        </h4>
      ) : (
        <span />
      )}
      {right && <span className="flex-shrink-0">{right}</span>}
    </div>
  )
}
